import { useEffect, useRef, useState } from 'react';
import { Icon } from './Icon.tsx';
import type { IconName } from './Icon.tsx';

/**
 * 오버레이 세 종류.
 *
 * 셋은 명확히 다른 물건입니다 (design/components.md 4절). 섞어 쓰지 마세요.
 *   Sheet   현재 화면에 대한 동작 선택. 아래로 끌어 닫음 (C1-4 필터)
 *   Modal   별개 대상의 정보. 크기 고정 (기획서 2.1.1 명시 — D2 친구 팝업)
 *   Alert   짧은 물음. 내용에 맞춰 줄어듭니다
 *
 * 셋 다 탭바 위에 뜹니다 — 뒤에 탭바가 보여야 맥락이 유지됩니다.
 * 계층 탐색용 Drawer 가 있었지만, 영역 서랍이 사라지면서 함께 없어졌습니다.
 *
 * FabMenu(C1-5)도 같은 스크림 위에 뜨지만 셋 중 하나가 아닙니다 — 화면을 덮는
 * 판이 아니라 ＋ 버튼 자신이 펼쳐지는 것이라, 여기 함께 두되 이름을 나눕니다.
 */

/** Esc 로 닫기. 웹뷰엔 키보드가 없지만 개발 중 브라우저에서 편합니다. */
function useEscape(onClose: () => void) {
  const saved = useRef(onClose);
  saved.current = onClose;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') saved.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
}

interface SheetProps {
  title?: string;
  action?: { label: string; onClick: () => void };
  onClose: () => void;
  children: React.ReactNode;
}

export function Sheet({ title, action, onClose, children }: SheetProps) {
  useEscape(onClose);
  return (
    <>
      <button type="button" className="scrim" aria-label="닫기" onClick={onClose} />
      <div className="sheet" role="dialog" aria-modal="true" aria-label={title}>
        <span className="sheet__grab" />
        {(title || action) && (
          <div className="sheet__head">
            {title && <b>{title}</b>}
            {action && (
              <button type="button" className="sheet__action" onClick={action.onClick}>
                {action.label}
              </button>
            )}
          </div>
        )}
        {children}
      </div>
    </>
  );
}

interface ModalProps {
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  label: string;
}

export function Modal({ onClose, children, footer, label }: ModalProps) {
  useEscape(onClose);
  return (
    <>
      <button type="button" className="scrim" aria-label="닫기" onClick={onClose} />
      {/* 크기 고정 — 기획서 2.1.1. 내용이 길어도 레이아웃이 흔들리지 않습니다 */}
      <div className="modal" role="dialog" aria-modal="true" aria-label={label}>
        <button type="button" className="modal__close" aria-label="닫기" onClick={onClose}>
          <Icon name="close" size={20} />
        </button>
        {children}
        {footer && <div className="modal__foot">{footer}</div>}
      </div>
    </>
  );
}

interface AlertProps {
  title: string;
  /** 배경 · ✕ 로 닫는 길. 확인 팝업에서는 «취소»와 같은 뜻입니다 */
  onClose: () => void;
  children?: React.ReactNode;
  actions: React.ReactNode;
}

/**
 * 알림 · 확인 팝업 — C2 나가기 · C4 삭제 확인 · C4-1 대표 상한.
 *
 * Modal 과 다른 물건입니다. Modal 은 기획서 2.1.1 대로 크기가 고정된 정보 창이고,
 * 이것은 내용에 맞춰 줄어드는 짧은 물음입니다. 한 문장 물어보려고 화면 3분의 2를
 * 차지하면 무엇을 묻는지가 오히려 흐려집니다.
 *
 * 닫는 길은 셋(배경 · ✕ · 취소), 진행하는 길은 actions 안의 버튼 하나입니다.
 * 셋 다 onClose 로 모이므로 "배경을 눌렀더니 지워졌다"가 생길 수 없습니다.
 */
export function Alert({ title, onClose, children, actions }: AlertProps) {
  useEscape(onClose);
  return (
    <>
      <button type="button" className="scrim" aria-label="닫기" onClick={onClose} />
      <div
        className="alert"
        role="alertdialog"
        aria-modal="true"
        aria-label={title}
      >
        <button type="button" className="alert__close" aria-label="닫기" onClick={onClose}>
          <Icon name="close" size={18} />
        </button>
        <p className="alert__title">{title}</p>
        {children && <div className="alert__text">{children}</div>}
        <div className="alert__actions">{actions}</div>
      </div>
    </>
  );
}

/* ── C1-5 — ＋ 를 누른 뒤 ─────────────────────────────────────── */

export interface FabAction {
  icon: IconName;
  label: string;
  onClick: () => void;
}

/**
 * ＋ 와 그 위로 떠오르는 동작 버튼.
 *
 * **＋ 는 작성 화면을 바로 열지 않습니다.** 한 번 눌러 「새 기도 노트」를 띄우고,
 * 그것을 눌러야 C2 가 열립니다 — 목록을 훑다 스친 손가락 하나로 빈 노트가
 * 만들어지지 않게 한 칸을 둔 것입니다.
 *
 * 그 한 칸이 나중에 값을 합니다. ＋ 로 만들 것이 늘면 이 자리에 위로 쌓이면 되고,
 * 화면을 새로 열거나 메뉴를 따로 만들지 않아도 됩니다. 지금은 하나뿐이라 버튼도 하나입니다.
 *
 * 닫는 길은 셋 — ✕ · 어두운 배경 · Esc. ＋ 가 자리를 옮기지 않고 ✕ 로만 바뀌므로
 * 열고 닫는 일이 엄지 하나 안에서 끝납니다. 여기서는 아무것도 만들어지지 않습니다.
 */
export function FabMenu({ label, actions }: { label: string; actions: FabAction[] }) {
  const [open, setOpen] = useState(false);
  useEscape(() => setOpen(false));

  return (
    <>
      {open && (
        <>
          {/* 어둡게 덮되 목록은 비쳐 보입니다 — 필터 시트와 같은 규칙 */}
          <button
            type="button"
            className="scrim"
            aria-label="닫기"
            onClick={() => setOpen(false)}
          />
          {/* 아래에서 위로 쌓습니다 — 첫 번째가 ＋ 에 가장 가깝습니다 */}
          <div className="fab-menu">
            {actions.map((a) => (
              <button
                key={a.label}
                type="button"
                className="fab-menu__item"
                onClick={() => {
                  setOpen(false);
                  a.onClick();
                }}
              >
                <Icon name={a.icon} size={16} />
                {a.label}
              </button>
            ))}
          </div>
        </>
      )}

      <button
        type="button"
        className={open ? 'fab fab--open' : 'fab'}
        aria-label={open ? '닫기' : label}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <Icon name={open ? 'close' : 'plus'} size={23} />
      </button>
    </>
  );
}
